import { CalendarEventRef } from '@griply/types';
import { toExternalEventBoundary } from '@griply/utils';
import { ExternalCalendarEvent } from 'shared/types/external-calendar-event';

import { ExtendedProps } from './extended-props';

export type ExternalEventDragRequest = {
  eventRef: CalendarEventRef;
  isRecurring: boolean;
  // undefined on an older backend, so only an explicit `false` may refuse the move
  isOrganizer: boolean | undefined;
  // the provider's own page for the event — where a refused move has to be made instead
  url: string | undefined;
  allDay: boolean;
  start: Date;
  // FullCalendar leaves this null on a zero-length event; all-day ends are exclusive
  end: Date | null;
  // the same boundaries in the cached-event shape, so the grid can paint before the write lands
  optimistic: Pick<ExternalCalendarEvent, 'start' | 'end'>;
};

export type DraggedExternalEvent = {
  extendedProps: ExtendedProps;
  start: Date | null;
  end: Date | null;
  allDay: boolean;
};

export const buildExternalEventDragRequest = (
  event: DraggedExternalEvent,
): ExternalEventDragRequest | null => {
  const {
    externalId,
    connectionId,
    calendarId,
    isRecurring,
    editable,
    isOrganizer,
    url,
  } = event.extendedProps;

  if (!externalId || !connectionId || !calendarId || !editable) {
    return null;
  }

  const { start, end, allDay } = event;
  if (!start) {
    return null;
  }

  return {
    eventRef: { connectionId, calendarId, eventId: externalId },
    isRecurring: !!isRecurring,
    isOrganizer,
    url,
    allDay,
    start,
    end,
    optimistic: {
      start: toExternalEventBoundary(allDay, start),
      end: toExternalEventBoundary(allDay, end ?? start),
    },
  };
};
